/**
 * Centrálny prekladač chýb do slovenčiny.
 *
 * Použitie v store/komponente:
 *   import { getErrorMessage } from '../utils/errorMessages';
 *   ...
 *   catch (error) {
 *     set({ error: getErrorMessage(error, 'Nepodarilo sa načítať stĺpiky.') });
 *   }
 *
 * Rozozná axios aj fetch chyby, HTTP stavy, sieťové výpadky a surové
 * anglické/technické hlášky nahradí zrozumiteľným slovenským textom.
 */

// Známe technické/anglické reťazce, ktoré nikdy nechceme ukázať používateľovi.
const TECHNICAL_PATTERNS = [
  /did not match the expected pattern/i,
  /failed to fetch/i,
  /networkerror/i,
  /load failed/i,
  /unexpected token/i,
  /json/i,
  /^\[object/i,
];

// Preklad častých anglických backend hlášok na slovenské.
const KNOWN_MESSAGES: Array<[RegExp, string]> = [
  [/incorrect (email|username) or password|invalid credentials/i, 'Nesprávny email alebo heslo.'],
  [/inactive user|user is not active/i, 'Účet je deaktivovaný. Kontaktujte administrátora.'],
  [/already (exists|registered)|duplicate/i, 'Záznam s týmito údajmi už existuje.'],
  [/not found/i, 'Záznam sa nenašiel.'],
  [/not enough permissions|forbidden|admin only/i, 'Nemáte oprávnenie na túto akciu.'],
  [/could not validate credentials|token/i, 'Vypršala platnosť prihlásenia. Prihláste sa znova.'],
];

function looksTechnical(msg: string): boolean {
  if (!msg) return true;
  return TECHNICAL_PATTERNS.some((re) => re.test(msg));
}

function translateKnown(msg: string): string | null {
  for (const [re, sk] of KNOWN_MESSAGES) {
    if (re.test(msg)) return sk;
  }
  return null;
}

/**
 * @param error   zachytená chyba (axios error, fetch error, Error, string)
 * @param fallback slovenská hláška špecifická pre akciu (napr. "Nepodarilo sa uložiť stĺpik.")
 */
export function getErrorMessage(error: any, fallback = 'Nastala chyba. Skúste to znova.'): string {
  // 1) Sieťová chyba / server nedostupný (axios bez response, alebo fetch "Failed to fetch")
  const isAxios = error && typeof error === 'object' && 'isAxiosError' in error;
  if (isAxios && !error.response) {
    return 'Server je nedostupný. Skontrolujte pripojenie a skúste znova.';
  }
  if (error?.message && /failed to fetch|network ?error|load failed/i.test(error.message)) {
    return 'Server je nedostupný. Skontrolujte pripojenie a skúste znova.';
  }

  // 2) HTTP stav (axios: error.response.status)
  const status: number | undefined = error?.response?.status;
  const detailRaw = error?.response?.data?.detail;
  // detail môže byť string alebo pole (FastAPI validačné chyby) — zoberieme prvý zmysluplný text
  let detail: string | undefined;
  if (typeof detailRaw === 'string') detail = detailRaw;
  else if (Array.isArray(detailRaw) && detailRaw.length) detail = detailRaw[0]?.msg;

  if (status) {
    switch (status) {
      case 400:
      case 422:
        // validačná chyba — ak backend poslal zmysluplný (netechnický) text, prelož/ukáž ho, inak fallback
        if (detail && !looksTechnical(detail)) return translateKnown(detail) || detail;
        return fallback;
      case 401:
        return translateKnown(detail || '') || 'Nesprávny email alebo heslo.';
      case 403:
        return 'Nemáte oprávnenie na túto akciu.';
      case 404:
        return 'Záznam sa nenašiel. Možno bol medzitým zmazaný.';
      case 409:
        return translateKnown(detail || '') || 'Záznam s týmito údajmi už existuje.';
      case 500:
      case 502:
      case 503:
        return 'Chyba na strane servera. Skúste to o chvíľu znova.';
      default:
        if (detail && !looksTechnical(detail)) return translateKnown(detail) || detail;
        return fallback;
    }
  }

  // 3) Bežná Error/string chyba
  const msg = typeof error === 'string' ? error : error?.message;
  if (msg && !looksTechnical(msg)) {
    return translateKnown(msg) || msg;
  }

  // 4) Nič použiteľné — slovenský fallback
  return fallback;
}
